import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
  IconButton,
  Checkbox,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Approved_icon from "assets/images/Approved.svg";
import notApproved_icon from "assets/images/noteApprove.svg";
import waitting_icon from "assets/images/waitting.svg";
import { Flex } from "antd";
import { Check, Clear } from "@mui/icons-material";
import api from "_api";
import { useAuth } from "../../hooks/useAuth";
import moment from "moment";

// Styled components for custom styling
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "10px",
  borderRadius: "8px",
  // minWidth: "100%",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(90deg, #00BB9F 0%, #6CD8BF 100%)",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  color: "#FFFFFF",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const BodyTableCell = styled(TableCell)(({ theme, idx }) => ({
  backgroundColor: idx % 2 === 0 ? theme.palette.common.white : "#ffffff",
  paddingLeft: "16px",
  paddingRight: "16px",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const NestedTableCell = styled(TableCell)(({ theme, idx }) => ({
  backgroundColor: idx % 2 === 0 ? theme.palette.common.white : "#ffffff",
  border: "1px solid #e0e0e0",
  padding: "8px",
  paddingLeft: "16px",
  paddingRight: "16px",
  width: "150px", // Set fixed width for each nested table cell
}));

const CustomTable = ({
  columns,
  data,
  page = 0,
  rowsPerPage = 10,
  columnsInside,
  hiddenColumns = [],
  searchTerm,
  setDataLength = () => {},
  fetchData = () => {},
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [selected, setSelected] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const { role } = useAuth();
  console.log({ columnsInside })

  // console.log("columns", columns);

  // Function to handle row expansion/collapse
  const handleExpandClick = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleCheckboxChange = (id) => {
    setSelected((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Function to handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Sort data based on sortColumn and sortDirection
  const sortedData = useMemo(() => {
    let filtered = data;

    // ฟังก์ชันการกรองข้อมูล
    if (searchTerm && Object.values(searchTerm).some((val) => val !== "")) {
      filtered = filtered.filter((row) => {
        // ตรวจสอบกรณีที่คีย์เป็น 'search'
        if (searchTerm.search) {
          const term = searchTerm.search.toLowerCase();
          // ค้นหาค่าทุกคอลัมน์ที่ไม่ถูกซ่อน
          return columns.some((column) => {
            if (hiddenColumns.includes(column.accessor)) return false;
            const value = row[column.accessor]?.toString().toLowerCase() || "";
            return value.includes(term);
          });
        }

        // กรณีที่ไม่ใช่ 'search'
        return Object.entries(searchTerm).every(([key, term]) => {
          if (term !== "") {
            const columnValue = row[key]?.toString().toLowerCase() || "";
            return columnValue.includes(term.toLowerCase());
          }
          return true;
        });
      });
    }

    // console.log("แสดงข้อมูล Filter", filtered);

    // การจัดเรียงข้อมูล
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        // Sorting based on type of data
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sortDirection === "asc"
            ? aValue.localeCompare(bValue, ["th", "en"], {
                sensitivity: "base",
              })
            : bValue.localeCompare(aValue, ["th", "en"], {
                sensitivity: "base",
              });
        }
      });
    }
    setDataLength(filtered?.length || 0);
    return filtered?.length > 0
      ? filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : filtered;
  }, [
    data,
    searchTerm,
    columns,
    hiddenColumns,
    sortColumn,
    sortDirection,
    page,
    rowsPerPage,
  ]);

  // Filter out hidden columns
  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.includes(column.accessor)
  );

  const parseCustomDateString = (dateString) => {
    if (dateString === null || dateString === "") {
      return { formattedDate: "-", formattedTime: "" };
    }
    // ตรวจสอบว่า dateString เป็น string หรือไม่
    if (typeof dateString !== "string" || !dateString.trim()) {
      console.warn("Invalid dateString:", dateString);
      return { formattedDate: "Invalid date", formattedTime: "" };
    }

    // Regex สำหรับจับค่ารูปแบบวันที่/เวลา
    const dateTimeRegex =
      /^(\d{2})\/(\d{2})\/(\d{4}) เวลา (\d{2}):(\d{2}):(\d{2}) น\.$/;
    const match = dateString.match(dateTimeRegex);

    if (!match) {
      console.warn("DateString does not match expected format:", dateString);
      return { formattedDate: "Invalid date", formattedTime: "" };
    }

    // แยกค่าที่ได้จาก regex
    const [, day, month, year, hours, minutes, seconds] = match;

    // สร้าง Date object
    const dateObj = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
    );

    if (isNaN(dateObj.getTime())) {
      console.error("Invalid Date object created from:", {
        day,
        month,
        year,
        hours,
        minutes,
        seconds,
      });
      return { formattedDate: "Invalid date", formattedTime: "" };
    }

    // ตัวเลือกการ format วันที่และเวลา
    const dateOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };

    // Format วันที่และเวลาให้อยู่ในรูปแบบที่ต้องการ
    const formattedDate = dateObj.toLocaleDateString("th-TH", dateOptions);
    const formattedTime = dateObj.toLocaleTimeString("th-TH", timeOptions);

    return { formattedDate, formattedTime };
  };

  //Display Status
  const getStatusDetails = (status) => {
    switch (status) {
      case "buy_product":
        return { label: "สินค้าที่ซื้อ", color: "#039be5" };
      case "pawn_product":
        return { label: "สินค้าขายฝาก", color: "#D32F2F" };
      default:
        return { label: "ไม่ทราบสถานะ", color: "default" };
    }
  };

  // Display Order Type
  const getOrderType = (order_type) => {
    switch (order_type) {
      case "Order_sells":
        return { label: "ขาย", color: "#D32F2F" };
      case "Order_items":
        return { label: "ซื้อ", color: "#2E7D32" };
      case "order_pawns":
        return { label: "ฝาก", color: "#039be5" };
      case "order_borrows":
        return { label: "ยืม/คืน", color: "#2E7D32" };
      default:
        return { label: "ไม่ทราบสถานะ", color: "default" };
    }
  };


  //Status Order
  const getStatusOrders = (status) => {
    switch (status) {
      case 1:
        return { label: "ขายฝาก", color: "#039be5" };
      case 2:
        return { label: "ไถ่ถอน", color: "#FFA600" };
      case 3:
        return { label: "พร้อมขาย", color: "#2E7D32" };
      case 4:
        return { label: "ขายแล้ว", color: "#D32F2F" };
      case 5:
        return { label: "ถูกยืม", color: "#2E7D32" };
      case 6:
        return { label: "คืนแล้ว", color: "#2196F3" };
      case 7:
        return { label: "แก้ไขข้อมูลสินค้า", color: "default" };
      case 8:
        return { label: "ปรับต้นทุน", color: "default" };
      case 9:
        return { label: "ยกเลิกรายการ", color: "default" };
      case 10:
        return { label: "หลุดจำนำ", color: "#D32F2F" };
      default:
        return { label: "ไม่ทราบสถานะ", color: "default" };
    }
  };

  //Display Type
  const getStatusMarketplace = (market_status) => {
    switch (market_status) {
      case "active":
        return { label: "วางขายแล้ว", color: "#66bb6a" };
      default:
        return { label: "ยังไม่วางขาย", color: "red" };
    }
  };

  const getUserStatus = (user_status) => {
    switch (user_status) {
      case 1:
        return { label: "ใช้งานอยู่", color: "#2E7D32" };
      case true:
        return { label: "ใช้งานอยู่", color: "#2E7D32" };
      default:
        return { label: "ไม่ใช้งาน", color: "red " };
    }
  };

  const getAllCompanyPackage = (company_status) => {
    switch (company_status) {
      case "ใช้งานอยู่":
        return { label: "ใช้งานอยู่", color: "#2E7D32" };
      case "ใกล้หมดอายุ":
        return { label: "ใกล้หมดอายุ", color: "#F18807" };
      case "หมดอายุ":
        return { label: "หมดอายุ", color: "#D32F2F" };
      default:
        return { label: "ปิดกิจการ", color: "#A1A1A1" };
    }
  };

  const getAllCompanyApprove = (approved) => {
    switch (approved) {
      case 1:
        return {
          label: "Approved",
          color: "green",
          icon: Approved_icon,
        };
      case 2:
        return {
          label: "Not Approved",
          color: "red",
          icon: notApproved_icon,
        };
      default:
        return {
          label: "Waitting",
          color: "orange",
          icon: waitting_icon,
        };
    }
  };

  const getUserApproved = (approved) => {
    switch (approved) {
      case 1:
        return {
          label: "Approved",
          color: "green",
          icon: Approved_icon,
        };
      case 2:
        return {
          label: "Not Approved",
          color: "red",
          icon: notApproved_icon,
        };
      default:
        return {
          label: "Waitting",
          color: "orange",
          icon: waitting_icon,
        };
    }
  };

  const updateUserApproved = async (id, values) => {
    const updated = await api.super_admin.userApproved(id, values);
    if (updated.isStatus) {
      fetchData();
    }
  };

  return (
    <StyledTableContainer component={Paper}>
      <Table>
        {/* Table Head */}
        <StyledTableHead>
          <TableRow>
            {/* Render each visible column header */}
            {visibleColumns.map((column) => (
              <StyledTableCell
                key={column.accessor}
                width={column.width}
                // onClick={() =>
                //   !column.disableSortBy && handleSort(column.accessor)
                // }
                style={
                  {
                    // justifySelf: "center",
                    // cursor: !column.disableSortBy ? "pointer" : "default",
                  }
                }
              >
                {column.Header}
                {/* {!column.disableSortBy && sortColumn === column.accessor && (
                  <ArrowUpwardIcon
                    style={{
                      transform:
                        sortDirection === "asc"
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                      marginLeft: "5px",
                      fontSize: "16px",
                    }}
                  />
                )} */}
              </StyledTableCell>
            ))}
          </TableRow>
        </StyledTableHead>

        {/* Table Body */}
        <TableBody>
          {sortedData?.length > 0 ? (
            sortedData
              .filter((row) => !row.parentId)
              .map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow>
                    {visibleColumns.map((column, colIndex) => (
                      <BodyTableCell
                        key={colIndex}
                        idx={rowIndex}
                        width={column.width}
                      >
                        {column.accessor === "checkbox" ? (
                          <Checkbox
                            checked={selected[row.id] || false}
                            onChange={() => handleCheckboxChange(row.id)}
                          />
                        ) : column.accessor === "expand" ? (
                          <IconButton
                            onClick={() => handleExpandClick(row.id)}
                            style={{
                              transform:
                                expandedRow === row.id
                                  ? "rotate(-180deg)"
                                  : "none",
                              transition: "transform 0.2s ease-in-out",
                            }}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        ) : column.accessor === "company_name" ? (
                          <Tooltip title={row.company_name} arrow>
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: "100px", // Adjust this as needed for your layout
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.company_name}
                            </span>
                          </Tooltip>
                        ) : column.accessor === "product_type" ? (
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={getStatusDetails(row.product_type).label}
                              style={{
                                width: "100px",
                                backgroundColor: getStatusDetails(
                                  row.product_type
                                ).color,
                                color: "white",
                              }}
                            />
                          </Stack>
                              ) : column.accessor === "order_type" ? (
                                // <Stack direction="row" spacing={1}>
                                //   <Chip
                                //       label={getOrderType(row.order_type).label}
                                //     style={{
                                //       width: "100px",
                                //       backgroundColor: getOrderType(
                                //         row.order_type
                                //       ).color,
                                //       color: "white",
                                //     }}
                                //   />
                                // </Stack>
                                <Typography>{getOrderType(row.order_type).label}</Typography>
                              ): column.accessor === "order_status" ? (
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={
                                row.order_status === 5 &&
                                row.return_date &&
                                moment(
                                  row.return_date,
                                  "DD/MM/YYYY เวลา HH:mm:ss น."
                                ).toDate() < new Date()
                                  ? "เกินกำหนด"
                                  : getStatusOrders(row.order_status).label
                              }
                              style={{
                                width: "100px",
                                backgroundColor:
                                  row.order_status === 5 &&
                                  row.return_date &&
                                  moment(
                                    row.return_date,
                                    "DD/MM/YYYY เวลา HH:mm:ss น."
                                  ).toDate() < new Date()
                                    ? "#D32F2F"
                                    : getStatusOrders(row.order_status).color,
                                color: "white",
                              }}
                            />
                          </Stack>
                        ) : column.accessor === "market_status" ? (
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={
                                getStatusMarketplace(row.market_status).label
                              }
                              style={{
                                width: "100px",
                                backgroundColor: getStatusMarketplace(
                                  row.market_status
                                ).color,
                                color: "white",
                              }}
                            />
                          </Stack>
                        ) : column.accessor === "company_status" ? (
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={
                                getAllCompanyPackage(row.company_status).label
                              }
                              style={{
                                width: "100px",
                                backgroundColor: getAllCompanyPackage(
                                  row.company_status
                                ).color,
                                color: "white",
                              }}
                            />
                          </Stack>
                        ) : column.accessor === "approved" ? (
                          <Stack direction="row" spacing={1}>
                            <img
                              src={getAllCompanyApprove(row.approved).icon}
                              alt="status icon"
                            />
                          </Stack>
                        ) : column.accessor === "created_date" ||
                          column.accessor === "updated_date" ? (
                          <div>
                            {
                              parseCustomDateString(row[column.accessor])
                                .formattedDate
                            }
                            <div>
                              เวลา{" "}
                              {
                                parseCustomDateString(row[column.accessor])
                                  .formattedTime
                              }{" "}
                              น.
                            </div>
                          </div>
                        ) : column.accessor === "return_date" ||
                          column.accessor === "order_due_date" ? (
                          <div>
                            {
                              parseCustomDateString(row[column.accessor])
                                .formattedDate
                            }
                          </div>
                        ) : column.accessor === "actions" ? (
                          column.Cell({ row })
                        ) : column.accessor === "user_approved" ? (
                          <Stack direction="row" spacing={1}>
                            {row.user_approved != 0 || role == "SuperAdminM" ? (
                              <img
                                src={getUserApproved(row.user_approved).icon}
                                alt="status icon"
                              />
                            ) : (
                              <Flex gap={30}>
                                <div
                                  className="circlebtn green"
                                  onClick={() => updateUserApproved(row.id, 1)} // approved
                                >
                                  <Check color="success" />
                                </div>
                                <div
                                  className="circlebtn red"
                                  onClick={() => updateUserApproved(row.id, 2)} // not approved
                                >
                                  <Clear color="error" />
                                </div>
                              </Flex>
                            )}
                          </Stack>
                        ) : column.accessor === "status" ? (
                          <Chip
                            label={getUserStatus(row.status).label}
                            style={{
                              width: "100px",
                              background: getUserStatus(row.status).color,
                              color: "white",
                            }}
                          />
                        ) : column.accessor === "role" ? (
                          <Chip
                            label={row.role}
                            style={{
                              width: "100px",
                              background: "#2196F3",
                              color: "white",
                              textTransform: "capitalize",
                            }}
                          />
                        ) : column.accessor === "picture" ? (
                          row.picture && row.picture.length > 0 ? (
                            <img
                              src={row.picture[0].picture}
                              alt="รูปภาพ"
                              style={{
                                width: "150px",
                                height: "150px",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                            />
                          ) : (
                            <Box
                              style={{
                                width: "150px",
                                height: "150px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#F8F7FD",
                                color: "#B0B0B0",
                              }}
                            >
                              ไม่มีรูปภาพ
                            </Box>
                          )
                        ) : (
                          row[column.accessor]
                        )}
                      </BodyTableCell>
                    ))}
                  </TableRow>

                  {expandedRow === row.id && (
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={visibleColumns?.length}
                      >
                        <Collapse
                          in={expandedRow === row.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            margin={1}
                            sx={{
                              overflow: "auto",
                              padding: 2, // เพิ่ม padding เพื่อให้ขอบดูเท่ากัน
                            }}
                          >
                            {/* ข้อมูลลูกค้า */}
                            <Box
                              display="flex"
                              gap={3}
                              sx={{
                                mx: 2,
                                borderBottom: "1px solid #e0e0e0", // เพิ่มเส้นแบ่งระหว่างข้อมูลลูกค้ากับรายการสินค้า
                                paddingBottom: 2, // เพิ่ม padding ที่ด้านล่าง
                              }}
                            >
                              <div>
                                <Typography
                                  sx={{ color: "#000000", opacity: 0.4 }}
                                >
                                  ชื่อ-นามสกุล
                                </Typography>
                                <Typography>{row.customer_name}</Typography>
                              </div>
                              <div>
                                <Typography
                                  sx={{ color: "#000000", opacity: 0.4 }}
                                >
                                  เลขบัตรประชาชน:
                                </Typography>
                                <Typography>{row.idcard}</Typography>
                              </div>
                              <div>
                                <Typography
                                  sx={{ color: "#000000", opacity: 0.4 }}
                                >
                                  เบอร์โทรศัพท์
                                </Typography>
                                <Typography>
                                  {row.phonenumber || row.customer_phonenumber}
                                </Typography>
                              </div>
                            </Box>

                            {/* ตารางสินค้า */}
                            <Box
                              sx={{
                                border: "none",
                                "& .MuiTableCell-root": {
                                  border: "none",
                                },
                                paddingTop: 2, // เพิ่ม padding ที่ด้านบนของตารางสินค้า
                              }}
                            >
                              {/* Header */}
                              <TableHead>
                                <TableRow>
                                  {columnsInside.map((column, keyIndex) => (
                                    <NestedTableCell
                                      key={keyIndex}
                                      sx={{ color: "#000000", opacity: 0.4 }}
                                    >
                                      {column.Header === "ชื่อสินค้า"
                                        ? ""
                                        : column.Header}
                                    </NestedTableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              {/* Rows */}
                              <TableBody>
                                {/* รวมข้อมูลทั้งหมดจากหลายแหล่ง */}
                                {[
                                  ...(row.order_items || []),
                                  ...(row.Order_sells || []),
                                  ...(row.order_pawns || []),
                                  ...(row.order_borrows || []),
                                ].length > 0 ? (
                                  // หากมีข้อมูลใน order_items หรืออื่นๆ
                                  [
                                    ...(row.order_items || []),
                                    ...(row.Order_sells || []),
                                    ...(row.order_pawns || []),
                                    ...(row.order_borrows || []),
                                  ].map((item, itemIndex) => (
                                    <TableRow key={itemIndex}>
                                      {columnsInside.map((column) => (
                                        <NestedTableCell key={column.accessor}>
                                          {item[column.accessor]}
                                        </NestedTableCell>
                                      ))}
                                    </TableRow>
                                  ))
                                ) : (
                                  // หากไม่มีข้อมูลจากที่กล่าวถึง ให้แสดงข้อความว่าไม่มีข้อมูล
                                  <TableRow>
                                    {columnsInside.map((column) => (
                                      <NestedTableCell key={column.accessor}>
                                        {row[column.accessor]}
                                      </NestedTableCell>
                                    ))}
                                  </TableRow>
                                )}
                              </TableBody>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
          ) : (
            <TableCell
              colSpan={visibleColumns?.length}
              style={{ textAlign: "center" }}
            >
              ไม่มีข้อมูล
            </TableCell>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default CustomTable;
